/**
 * Popup Announcement Web Component
 * Displays a promotional popup with configurable timing and frequency.
 */

if (!customElements.get('popup-announcement')) {
  customElements.define(
    'popup-announcement',
    class PopupAnnouncement extends HTMLElement {
      constructor() {
        super();
        this.currentSlide = 0;
        this.slides = [];
        this.sectionId = '';
        this.frequency = 'session';
        this.delay = 500;
      }

      connectedCallback() {
        // Get configuration from data attributes
        this.sectionId = this.dataset.sectionId || '';
        this.frequency = this.dataset.frequency || 'session';
        this.delay = parseInt(this.dataset.delay, 10) || 500;

        // Generate storage key based on section ID
        this.storageKey = `popupAnnouncement_${this.sectionId}`;

        // Check if should show based on frequency setting
        if (this.hasBeenShown()) {
          this.remove();
          return;
        }

        // Cache DOM elements
        this.slides = this.querySelectorAll('.popup-announcement__slide');
        this.overlay = this.querySelector('.popup-announcement__overlay');
        this.closeBtn = this.querySelector('.popup-announcement__close');
        this.prevBtn = this.querySelector('.popup-announcement__nav--prev');
        this.nextBtn = this.querySelector('.popup-announcement__nav--next');

        // Bind event handlers
        this.bindEvents();

        // Show popup after configured delay
        setTimeout(() => this.show(), this.delay);
      }

      bindEvents() {
        // Close button
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', () => this.hide());
        }

        // Overlay click to close
        if (this.overlay) {
          this.overlay.addEventListener('click', () => this.hide());
        }

        // Navigation arrows
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.prevSlide());
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.nextSlide());
        }

        // Escape key to close
        this.handleKeyDown = this.handleKeyDown.bind(this);
        document.addEventListener('keydown', this.handleKeyDown);

        // Prevent scroll on touch devices when popup is open
        this.addEventListener('touchmove', (e) => {
          if (e.target === this.overlay) {
            e.preventDefault();
          }
        }, { passive: false });
      }

      handleKeyDown(event) {
        if (!this.hasAttribute('open')) return;

        switch (event.key) {
          case 'Escape':
            this.hide();
            break;
          case 'ArrowLeft':
            this.prevSlide();
            break;
          case 'ArrowRight':
            this.nextSlide();
            break;
        }
      }

      show() {
        this.setAttribute('open', '');
        document.body.classList.add('popup-announcement-open');

        // Focus the close button for accessibility
        if (this.closeBtn) {
          this.closeBtn.focus();
        }

        // Trap focus within the popup
        this.trapFocus();
      }

      hide() {
        // Add closing class for exit animation
        this.classList.add('closing');

        // Wait for animation to complete before removing
        setTimeout(() => {
          this.removeAttribute('open');
          this.classList.remove('closing');
          document.body.classList.remove('popup-announcement-open');

          // Mark as shown based on frequency setting
          this.markAsShown();

          // Clean up event listener
          document.removeEventListener('keydown', this.handleKeyDown);

          // Remove focus trap
          this.removeFocusTrap();

          // Dispatch custom event
          document.body.dispatchEvent(new CustomEvent('popupAnnouncementClosed'));
        }, 300); // Match CSS animation duration
      }

      nextSlide() {
        if (this.slides.length <= 1) return;

        this.slides[this.currentSlide].classList.remove('popup-announcement__slide--active');
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
        this.slides[this.currentSlide].classList.add('popup-announcement__slide--active');
      }

      prevSlide() {
        if (this.slides.length <= 1) return;

        this.slides[this.currentSlide].classList.remove('popup-announcement__slide--active');
        this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
        this.slides[this.currentSlide].classList.add('popup-announcement__slide--active');
      }

      goToSlide(index) {
        if (index < 0 || index >= this.slides.length) return;

        this.slides[this.currentSlide].classList.remove('popup-announcement__slide--active');
        this.currentSlide = index;
        this.slides[this.currentSlide].classList.add('popup-announcement__slide--active');
      }

      /**
       * Check if popup has been shown based on frequency setting
       * @returns {boolean} Whether popup should be hidden
       */
      hasBeenShown() {
        // "always" frequency = show every time
        if (this.frequency === 'always') {
          return false;
        }

        try {
          if (this.frequency === 'session') {
            // Check sessionStorage for session-based frequency
            return sessionStorage.getItem(this.storageKey) === 'true';
          }

          if (this.frequency === 'day') {
            // Check localStorage with timestamp for daily frequency
            const lastShown = localStorage.getItem(this.storageKey);
            if (!lastShown) return false;

            const lastShownDate = new Date(parseInt(lastShown, 10));
            const now = new Date();

            // Check if it's still the same calendar day
            return (
              lastShownDate.getFullYear() === now.getFullYear() &&
              lastShownDate.getMonth() === now.getMonth() &&
              lastShownDate.getDate() === now.getDate()
            );
          }
        } catch (e) {
          // Storage might be blocked, show popup
          return false;
        }

        return false;
      }

      /**
       * Mark popup as shown based on frequency setting
       */
      markAsShown() {
        // "always" frequency = don't store anything
        if (this.frequency === 'always') {
          return;
        }

        try {
          if (this.frequency === 'session') {
            // Store in sessionStorage
            sessionStorage.setItem(this.storageKey, 'true');
          }

          if (this.frequency === 'day') {
            // Store timestamp in localStorage
            localStorage.setItem(this.storageKey, Date.now().toString());
          }
        } catch (e) {
          // Storage might be blocked, continue anyway
        }
      }

      trapFocus() {
        const focusableElements = this.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        this.firstFocusable = focusableElements[0];
        this.lastFocusable = focusableElements[focusableElements.length - 1];

        this.handleFocusTrap = (e) => {
          if (e.key !== 'Tab') return;

          if (e.shiftKey) {
            if (document.activeElement === this.firstFocusable) {
              e.preventDefault();
              this.lastFocusable.focus();
            }
          } else {
            if (document.activeElement === this.lastFocusable) {
              e.preventDefault();
              this.firstFocusable.focus();
            }
          }
        };

        this.addEventListener('keydown', this.handleFocusTrap);
      }

      removeFocusTrap() {
        if (this.handleFocusTrap) {
          this.removeEventListener('keydown', this.handleFocusTrap);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeyDown);
        this.removeFocusTrap();
      }
    }
  );
}
